import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Card, Typography, Select, MenuItem, FormControl, InputLabel, 
  Stack, Chip, Divider, Alert, Button 
} from '@mui/material';
import { 
  Compare as CompareIcon, 
  Assessment as AssessmentIcon, 
  AttachMoney as MoneyIcon, 
  Token as TokenIcon,
  Done as DoneIcon,
  Remove as RemoveIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

import useStudioStore from '../../state/studioStore';
import { API } from '../../lib/api';

export default function DiffCore() {
  const { currentRun, currentVersion, pushEvent, setWorkflowComparison } = useStudioStore();

  const [versions, setVersions] = useState([]);
  const [baselineId, setBaselineId] = useState('');
  const [candidateId, setCandidateId] = useState('');
  const [diffData, setDiffData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [versionsLoading, setVersionsLoading] = useState(false);
  const diffInProgress = useRef(false);
  const autoDiffTriggered = useRef(false);

  // Load versions on mount - always load data
  useEffect(() => {
    const loadVersions = async () => {
      if (versionsLoading) return; // Prevent multiple calls
      setVersionsLoading(true);
      setError('');
      
      try {
        console.log('Diff: Loading prompt versions from API...');
        const allVersions = await API.prompts.allVersions();
        const decoratedVersions = allVersions.map(v => ({
          ...v,
          label: `${v.promptKey} v${v.version}${v.status ? ` (${v.status})` : ''}`
        }));

        console.log('Total versions loaded:', decoratedVersions.length);
        setVersions(decoratedVersions);
        
        // Auto-populate with current context if available
        if (currentVersion && decoratedVersions.length > 0) {
          setCandidateId(currentVersion.id);
          
          // Find the previous version for comparison
          const samePromptVersions = decoratedVersions
            .filter(v => v.promptKey === currentVersion.promptKey)
            .sort((a, b) => b.version - a.version);
          
          if (samePromptVersions.length >= 2 && !autoDiffTriggered.current) {
            const prevVersion = samePromptVersions[1]; // Second newest
            setBaselineId(prevVersion.id);
            
            // Auto-run comparison only once
            autoDiffTriggered.current = true;
            setTimeout(() => runDiff(), 500);
          }
        } else if (decoratedVersions.length >= 2) {
          // Fallback to first two versions
          setBaselineId(decoratedVersions[0].id);
          setCandidateId(decoratedVersions[1].id);
      }
        
      } catch (err) {
        console.error('Diff version loading failed:', err);
        setError('Failed to load versions: ' + err.message);
      } finally {
        setVersionsLoading(false);
      }
    };
    
    loadVersions();
  }, []); // Load once on mount

  // React to context changes
  useEffect(() => {
    if (currentVersion && versions.length > 0) {
      setCandidateId(currentVersion.id);
      
      // Find previous version for auto-comparison
      const samePromptVersions = versions
        .filter(v => v.promptKey === currentVersion.promptKey)
        .sort((a, b) => b.version - a.version);
      
      if (samePromptVersions.length >= 2) {
        const prevVersion = samePromptVersions[1];
        setBaselineId(prevVersion.id);
        
        pushEvent({
          type: 'diff',
          title: `Auto-comparing v${prevVersion.version} → v${currentVersion.version}`,
          meta: { baseline: prevVersion, candidate: currentVersion }
        });
      }
    }
  }, [currentVersion, versions, pushEvent]);

  const runDiff = async () => {
    if (diffInProgress.current) return; // Prevent recursive calls
    if (!baselineId || !candidateId) {
      setError('Please select both baseline and candidate versions');
      return;
    }

    diffInProgress.current = true;
    setLoading(true);
    setError('');

    try {
      const data = await API.prompts.diff(baselineId, candidateId);
      const normalized = {
        ...data,
        token_counts: data?.token_counts || {},
        cost_estimate: data?.cost_estimate || {},
        hunks: Array.isArray(data?.hunks) ? data.hunks : [],
      };
      setDiffData(normalized);

      // Share comparison results with workflow context
      setWorkflowComparison(normalized);

    } catch (err) {
      setError('Failed to generate diff: ' + err.message);
    } finally {
      setLoading(false);
      diffInProgress.current = false;
    }
  };

  const formatCostBadge = (cost, label, color = 'default') => (
    <Chip 
      icon={<MoneyIcon />}
      label={`${label}: $${Math.abs(cost).toFixed(6)}`}
      color={color}
      size="small"
      variant="outlined"
    />
  );

  const formatTokenBadge = (tokens, label, color = 'default') => (
    <Chip 
      icon={<TokenIcon />}
      label={`${label}: ${Math.abs(tokens)} tokens`}
      color={color}
      size="small" 
      variant="outlined"
    />
  );

  return (
    <Box sx={{ p: 4 }}>
      <Card sx={{ p: 4, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <CompareIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
          <Typography variant="h5" gutterBottom>Prompt Diff</Typography>
        </Stack>
        <Typography variant="body2" sx={{ opacity: 0.8, mb: 3 }}>
          Compare prompt versions with inline diffs, token counts, and cost estimates
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <FormControl sx={{ minWidth: 250 }} disabled={versionsLoading}>
            <InputLabel>Baseline Version</InputLabel>
            <Select 
              value={baselineId} 
              onChange={e => setBaselineId(e.target.value)} 
              label="Baseline Version"
            >
              {versionsLoading && (
                <MenuItem disabled>Loading versions...</MenuItem>
              )}
              {!versionsLoading && versions.length === 0 && (
                <MenuItem disabled>No versions found</MenuItem>
              )}
              {versions.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 250 }} disabled={versionsLoading}>
            <InputLabel>Candidate Version</InputLabel>
            <Select 
              value={candidateId} 
              onChange={e => setCandidateId(e.target.value)} 
              label="Candidate Version"
            >
              {versionsLoading && (
                <MenuItem disabled>Loading versions...</MenuItem>
              )}
              {!versionsLoading && versions.length === 0 && (
                <MenuItem disabled>No versions found</MenuItem>
              )}
              {versions.map(v => (
                <MenuItem key={v.id} value={v.id}>{v.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button 
            variant="contained" 
            onClick={() => runDiff()}
            disabled={loading || !baselineId || !candidateId}
            size="large"
            startIcon={<CompareIcon />}

          >
            {loading ? 'Analyzing...' : 'Compare'}
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      </Card>

      {diffData && (() => {
        const tokenCounts = diffData?.token_counts || {};
        const costEstimate = diffData?.cost_estimate || {};
        const hunks = Array.isArray(diffData?.hunks) ? diffData.hunks : [];

        const tokenDelta = tokenCounts.delta ?? 0;
        const costDelta = costEstimate.delta_usd ?? 0;

        return (
          <>
            {/* Metrics Overview */}
            <Card sx={{ p: 3, mb: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <AssessmentIcon sx={{ color: 'info.main' }} />
                <Typography variant="h6">Impact Analysis</Typography>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ minWidth: 100 }}>Tokens:</Typography>
                {formatTokenBadge(tokenCounts.baseline ?? 0, 'Baseline', 'info')}
                {formatTokenBadge(tokenCounts.candidate ?? 0, 'Candidate', 'info')}
                {formatTokenBadge(
                  tokenDelta,
                  tokenDelta >= 0 ? '+Delta' : 'Delta',
                  tokenDelta >= 0 ? 'warning' : 'success'
                )}
              </Stack>

              <Stack direction="row" spacing={2}>
                <Typography variant="subtitle2" sx={{ minWidth: 100 }}>Cost:</Typography>
                {formatCostBadge(costEstimate.baseline_usd ?? 0, 'Baseline', 'info')}
                {formatCostBadge(costEstimate.candidate_usd ?? 0, 'Candidate', 'info')}
                {formatCostBadge(
                  costDelta,
                  costDelta >= 0 ? '+Delta' : 'Delta',
                  costDelta >= 0 ? 'error' : 'success'
                )}
              </Stack>
            </Card>

            {/* Diff Hunks */}
            {hunks.length > 0 ? (
              <Card sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                  <CompareIcon sx={{ color: 'primary.main' }} />
                  <Typography variant="h6">Changes</Typography>
                </Stack>

                {hunks.map((hunk, index) => (
                  <Box key={index} sx={{ mb: 3 }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                      <TokenIcon sx={{ color: 'warning.main' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {hunk.path || `Fragment ${index + 1}`}
                      </Typography>
                    </Stack>

                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <RemoveIcon sx={{ color: 'error.main', fontSize: 16 }} />
                          <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                            Baseline
                          </Typography>
                        </Stack>
                        <Box sx={{
                          border: '1px solid',
                          borderColor: 'error.main',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}>
                          <SyntaxHighlighter
                            language="text"
                            style={oneDark}
                            customStyle={{
                              margin: 0,
                              fontSize: '0.875rem',
                              maxHeight: '200px',
                              background: 'rgba(244, 67, 54, 0.05)'
                            }}
                          >
                            {hunk.a || '(empty)'}
                          </SyntaxHighlighter>
                        </Box>
                      </Box>

                      <Box>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <AddIcon sx={{ color: 'success.main', fontSize: 16 }} />
                          <Typography variant="caption" color="success.main" sx={{ fontWeight: 600 }}>
                            Candidate
                          </Typography>
                        </Stack>
                        <Box sx={{
                          border: '1px solid',
                          borderColor: 'success.main',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}>
                          <SyntaxHighlighter
                            language="text"
                            style={oneDark}
                            customStyle={{
                              margin: 0,
                              fontSize: '0.875rem',
                              maxHeight: '200px',
                              background: 'rgba(76, 175, 80, 0.05)'
                            }}
                          >
                            {hunk.b || '(empty)'}
                          </SyntaxHighlighter>
                        </Box>
                      </Box>
                    </Box>

                    {hunk.diff && (
                      <Box>
                        <Typography variant="caption" sx={{ opacity: 0.7 }}>
                          Unified diff:
                        </Typography>
                        <Box sx={{
                          border: '1px solid rgba(255,255,255,0.2)',
                          borderRadius: 1,
                          overflow: 'hidden'
                        }}>
                          <SyntaxHighlighter
                            language="diff"
                            style={oneDark}
                            customStyle={{
                              margin: 0,
                              fontSize: '0.75rem',
                              maxHeight: '150px'
                            }}
                          >
                            {hunk.diff}
                          </SyntaxHighlighter>
                        </Box>
                      </Box>
                    )}

                    {index < hunks.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Card>
            ) : (
              <Card sx={{ p: 3 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                  <DoneIcon sx={{ color: 'success.main' }} />
                  <Typography variant="h6">No Changes</Typography>
                </Stack>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  The selected versions have identical content.
                </Typography>
              </Card>
            )}
          </>
        );
      })()}
    </Box>
  );
}
